import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Check, 
  X, 
  Clock, 
  User,
  HelpCircle,
  BookOpen,
  Lightbulb,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const getCategoryColor = (category) => {
  switch(category) {
    case 'question': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'story': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'thought': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const getCategoryIcon = (category) => {
  switch(category) {
    case 'question': return <HelpCircle className="w-4 h-4" />;
    case 'story': return <BookOpen className="w-4 h-4" />;
    case 'thought': return <Lightbulb className="w-4 h-4" />;
    default: return <MessageSquare className="w-4 h-4" />;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const ForumModeration = () => {
  const { user } = useAuth();
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(null);
  const [declineReason, setDeclineReason] = useState('violated community policies');

  const fetchPendingPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/forums/admin/pending');
      setPendingPosts(response.data);
    } catch (error) {
      toast.error('Failed to load pending posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPosts();
  }, [fetchPendingPosts]);

  const handleApprove = async (postId) => {
    try {
      setProcessingId(postId);
      await api.post(`/forums/admin/approve/${postId}`);
      toast.success('Post approved and published!');
      setPendingPosts(pendingPosts.filter(p => p.id !== postId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve post');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (postId) => {
    try {
      setProcessingId(postId);
      await api.post(`/forums/admin/decline/${postId}?reason=${encodeURIComponent(declineReason)}`);
      toast.success('Post declined. User has been notified via email.');
      setPendingPosts(pendingPosts.filter(p => p.id !== postId));
      setShowDeclineModal(null);
      setDeclineReason('violated community policies');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline post');
    } finally {
      setProcessingId(null);
    }
  };

  // Redirect if not admin
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="forum-moderation-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Forum Post Moderation</h1>
            <p className="text-muted-foreground">Review and approve user forum posts before they become public</p>
          </div>
          <button
            onClick={fetchPendingPosts}
            className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-xl hover:border-primary transition-colors"
            data-testid="refresh-posts"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pendingPosts.length}</p>
              <p className="text-sm text-muted-foreground">Posts awaiting review</p>
            </div>
          </div>
        </div>

        {/* Pending Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : pendingPosts.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border">
            <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">All caught up!</p>
            <p className="text-muted-foreground">No pending posts to review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingPosts.map(post => (
              <div
                key={post.id}
                className="bg-card border border-yellow-500/30 rounded-2xl p-6"
                data-testid={`pending-post-${post.id}`}
              >
                {/* Post Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(post.category)}`}>
                      {getCategoryIcon(post.category)}
                      {post.category}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      ⏳ Pending Review
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">{formatDate(post.created_at)}</span>
                </div>

                {/* Author Info */}
                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>Posted by <strong className="text-foreground">{post.author_name}</strong></span>
                </div>

                {/* Post Content */}
                <h3 className="text-lg font-semibold text-foreground mb-2">{post.title}</h3>
                <p className="text-muted-foreground mb-4 whitespace-pre-wrap">{post.content}</p>

                {/* Tags */}
                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-muted rounded-lg text-xs text-muted-foreground">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <button
                    onClick={() => handleApprove(post.id)}
                    disabled={processingId === post.id}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                    data-testid={`approve-post-${post.id}`}
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowDeclineModal(post.id)}
                    disabled={processingId === post.id}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                    data-testid={`decline-post-${post.id}`}
                  >
                    <X className="w-4 h-4" />
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Decline Modal */}
        {showDeclineModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-foreground mb-4">Decline Post</h3>
              <p className="text-muted-foreground mb-4">
                The user will be notified via email that their post has been declined.
              </p>
              
              <label className="block text-sm font-medium text-foreground mb-2">
                Reason for declining
              </label>
              <select
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary mb-4"
              >
                <option value="violated community policies">Violated community policies</option>
                <option value="contains inappropriate content">Contains inappropriate content</option>
                <option value="spam or promotional content">Spam or promotional content</option>
                <option value="contains harmful or triggering content">Contains harmful or triggering content</option>
                <option value="duplicate or repetitive post">Duplicate or repetitive post</option>
              </select>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeclineModal(null)}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDecline(showDeclineModal)}
                  disabled={processingId === showDeclineModal}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50"
                >
                  Decline & Notify User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForumModeration;
